// Margin Calculator — types + margin maths (Phase 3).
//
// A computed table of each bookmaker's margin (overround) per market, with the
// tightest margin per market highlighted. Pure and framework-free (data read via
// the app-web accessor); the React component and the legacy renderer must
// produce identical rows — the visual A/B guards it. Reuses the betting-market
// odds shapes from valueIndicator.ts.

import type { Outcome3, OverUnder } from "./valueIndicator.js";

/** Odds for one market/bookmaker: 3-way (1X2) or 2-way (O/U, BTTS). */
export type MarginOdds = Outcome3 | OverUnder;

/** One fixture's markets: market → bookmaker → odds. */
export type MarginFixture = Record<string, Record<string, MarginOdds>>;

/** Margin data keyed by fixture. */
export type MarginCalculatorData = Record<string, MarginFixture>;

/** The three markets shown, in column order. */
export const MARGIN_MARKETS = ["1X2", "O/U 2.5", "BTTS"] as const;

/** Bookmaker display info (row order + badge). Note: colours are this widget's
 *  own — they differ slightly from other betting widgets, so don't unify. */
export interface BookmakerInfo {
  name: string;
  abbr: string;
  colour: string;
}

export const MARGIN_BOOKMAKERS: readonly BookmakerInfo[] = [
  { name: "Pinnacle", abbr: "PN", colour: "#1c3f6e" },
  { name: "Betfair", abbr: "BF", colour: "#c9a000" },
  { name: "Ladbrokes", abbr: "LD", colour: "#e30613" },
  { name: "Paddy Power", abbr: "PP", colour: "#00a651" },
];

const DEFAULT_FIXTURE = "Arsenal v Everton";

/** Implied probability (%) from decimal odds. */
function implied(odds: number): number {
  return 100 / odds;
}

/** Bookmaker margin (overround, %): sum of implied probabilities − 100. */
export function computeMargin(odds: MarginOdds, market: string): number {
  if (market === "1X2") {
    const d = odds as Outcome3;
    return implied(d.home) + implied(d.draw) + implied(d.away) - 100;
  }
  const d = odds as OverUnder;
  return implied(d[0]) + implied(d[1]) - 100;
}

/** One margin cell. */
export interface MarginCell {
  market: string;
  margin: number;
  /** True when this is (within 0.05 of) the tightest margin in the market. */
  isBest: boolean;
}

/** One bookmaker row. */
export interface MarginRow {
  bookmaker: string;
  abbr: string;
  colour: string;
  cells: MarginCell[];
}

export interface MarginTable {
  markets: string[];
  rows: MarginRow[];
}

/**
 * Compute the margin table for a fixture: a row per bookmaker, a cell per
 * market, with the tightest margin per market flagged `isBest`. Falls back to
 * Arsenal v Everton for an unknown fixture.
 */
export function shapeMarginCalculator(
  fixture: string,
  data: MarginCalculatorData
): MarginTable {
  const fix = fixture || DEFAULT_FIXTURE;
  const fixData = data[fix] ?? data[DEFAULT_FIXTURE];
  const markets = [...MARGIN_MARKETS];

  // Tightest (lowest) margin per market, across bookmakers.
  const best: Record<string, number> = {};
  for (const m of markets) {
    let min = Infinity;
    for (const bk of MARGIN_BOOKMAKERS) {
      const odds = fixData?.[m]?.[bk.name];
      if (odds) min = Math.min(min, computeMargin(odds, m));
    }
    best[m] = min;
  }

  const rows = MARGIN_BOOKMAKERS.map((bk) => ({
    bookmaker: bk.name,
    abbr: bk.abbr,
    colour: bk.colour,
    cells: markets.map((m) => {
      const odds = fixData?.[m]?.[bk.name];
      const margin = odds ? computeMargin(odds, m) : 0;
      return { market: m, margin, isBest: Math.abs(margin - best[m]!) < 0.05 };
    }),
  }));

  return { markets, rows };
}

/** Cell colour: green when tightest, red when wide (>8%), muted otherwise. */
export function marginCellColor(margin: number, isBest: boolean): string {
  return isBest ? "#2ecc71" : margin > 8 ? "#e74c3c" : "var(--bm-text)";
}
